import { useEffect, useState } from "react";

export type UserLocation = {
  latitude: number;
  longitude: number;
};

const LOCATION_STORAGE_KEY = "tnp_user_location";
const LOCATION_AUTHORIZED_STORAGE_KEY = "tnp_user_location_authorized";
const LOCATION_PROMPT_STORAGE_KEY = "tnp_user_location_prompted";
const LOCATION_UPDATED_EVENT = "tnp:user-location-updated";

let automaticPromptStarted = false;

function isUsableLocation(value: unknown): value is UserLocation {
  const location = value as UserLocation;
  return (
    Number.isFinite(Number(location?.latitude)) &&
    Number.isFinite(Number(location?.longitude))
  );
}

function safeLocalStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch (_err) {
    return null;
  }
}

function emitLocationUpdate() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(LOCATION_UPDATED_EVENT));
}

export function getStoredUserLocation(): UserLocation | null {
  const storage = safeLocalStorage();
  if (!storage) return null;
  if (storage.getItem(LOCATION_AUTHORIZED_STORAGE_KEY) !== "true") return null;
  try {
    const raw = storage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isUsableLocation(parsed)) return null;
    return {
      latitude: Number(parsed.latitude),
      longitude: Number(parsed.longitude),
    };
  } catch (_err) {
    return null;
  }
}

export async function getGeolocationPermissionState() {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return null;
  }
  try {
    const status = await navigator.permissions.query({
      name: "geolocation",
    } as PermissionDescriptor);
    return status.state;
  } catch (_err) {
    return null;
  }
}

export async function getStoredUserLocationForCurrentPermission() {
  const stored = getStoredUserLocation();
  if (!stored) return null;

  const permissionState = await getGeolocationPermissionState();
  if (permissionState && permissionState !== "granted") {
    clearUserLocation();
    return null;
  }

  return stored;
}

export function clearUserLocation() {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.removeItem(LOCATION_STORAGE_KEY);
  storage.removeItem(LOCATION_AUTHORIZED_STORAGE_KEY);
  emitLocationUpdate();
}

export function saveUserLocation(location: UserLocation) {
  if (!isUsableLocation(location)) return;
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.setItem(LOCATION_AUTHORIZED_STORAGE_KEY, "true");
  storage.setItem(
    LOCATION_STORAGE_KEY,
    JSON.stringify({
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
    }),
  );
  emitLocationUpdate();
}

export function hasPromptedForUserLocation() {
  const storage = safeLocalStorage();
  return storage?.getItem(LOCATION_PROMPT_STORAGE_KEY) === "true";
}

export function markUserLocationPrompted() {
  const storage = safeLocalStorage();
  storage?.setItem(LOCATION_PROMPT_STORAGE_KEY, "true");
}

export function requestCurrentUserLocation({
  force = false,
}: { force?: boolean } = {}) {
  return new Promise<UserLocation>((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("geolocation_unavailable"));
      return;
    }

    if (!force) markUserLocationPrompted();

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLocation = {
          latitude: Number(position.coords.latitude),
          longitude: Number(position.coords.longitude),
        };
        if (!isUsableLocation(nextLocation)) {
          reject(new Error("geolocation_invalid"));
          return;
        }
        saveUserLocation(nextLocation);
        resolve(nextLocation);
      },
      (error) => {
        clearUserLocation();
        reject(error);
      },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  });
}

export function requestUserLocationOnEntry() {
  if (automaticPromptStarted) return;
  automaticPromptStarted = true;
  void (async () => {
    const stored = getStoredUserLocation();
    if (stored) {
      const permissionState = await getGeolocationPermissionState();
      if (permissionState === "granted" || permissionState === null) return;
      clearUserLocation();
    }
    if (hasPromptedForUserLocation()) return;
    await requestCurrentUserLocation().catch(() => undefined);
  })();
}

export function useUserLocation() {
  const [location, setLocation] = useState<UserLocation | null>(null);

  useEffect(() => {
    let cancelled = false;
    const syncLocation = () => {
      void getStoredUserLocationForCurrentPermission().then((nextLocation) => {
        if (!cancelled) setLocation(nextLocation);
      });
    };
    syncLocation();
    window.addEventListener(LOCATION_UPDATED_EVENT, syncLocation);
    window.addEventListener("storage", syncLocation);
    return () => {
      cancelled = true;
      window.removeEventListener(LOCATION_UPDATED_EVENT, syncLocation);
      window.removeEventListener("storage", syncLocation);
    };
  }, []);

  return location;
}
